import { ComponentProps, FC, useMemo } from 'react';

import { Transaction } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Tooltip } from '@ravago/shared/radiance/components/tooltip/components/Tooltip/Tooltip';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { calculateTotalNegativeAmount, calculateTotalOpenAmount, calculateTotalPositiveDueAmount } from '../../../utils/invoice-amount.utils';
import { canSelectInvoice } from '../../../utils/invoice.utils';

type PaymentButtonVariant = 'selected' | 'open' | 'overdue';

type Props = {
   currency: string;
   openInvoices: Transaction[];
   selectedInvoiceIds: number[];
   canDeductCreditNotes: boolean;
   onPayClick: (type: PaymentButtonVariant) => void;
};

type PaymentButtonProps = {
   variant: PaymentButtonVariant;
   amount: number;
   totalAmount?: number;
   currency: string;
   disabled?: boolean;
   disabledMessage?: string;
   onPayClick: (type: PaymentButtonVariant) => void;
};

const paymentButtonTitleConfig: Record<PaymentButtonVariant, string> = {
   selected: 'invoices.invoice-summary.selected-title',
   open: 'invoices.invoice-summary.total-amount',
   overdue: 'invoices.invoice-summary.overdue-title'
};

const getPaymentButtonColors = (variant: PaymentButtonVariant, amount: number) => {
   let color: ComponentProps<typeof Typography>['color'];
   if (amount === 0) {
      color = 'subtle';
   } else if (variant === 'overdue') {
      color = 'error';
   } else {
      color = 'primary';
   }

   let backgroundColor: ComponentProps<typeof Box>['backgroundColor'];
   if (amount === 0 || variant === 'open') {
      backgroundColor = 'surface-2';
   } else if (variant === 'overdue') {
      backgroundColor = 'system-danger';
   } else if (variant === 'selected') {
      backgroundColor = 'system-info';
   }

   return { color, backgroundColor };
};

const getPaymentButtonLabel = (variant: PaymentButtonVariant) => {
   if (variant === 'selected') return 'invoices.invoice-summary.pay-selected';
   if (variant === 'open') return 'invoices.invoice-summary.pay-open';
   return 'invoices.invoice-summary.pay-overdue';
};

const PaymentButton = ({ variant, amount, totalAmount, currency, disabled, disabledMessage, onPayClick }: PaymentButtonProps) => {
   const translate = useTranslations();
   const format = useFormatter();

   const title = translate(paymentButtonTitleConfig[variant]);
   const { color, backgroundColor } = getPaymentButtonColors(variant, amount);

   const canPay = amount > 0 && !disabled;
   const showTotal = !!totalAmount && totalAmount !== amount;

   const PayButton = (
      <Button
         variant="tertiary"
         disabled={!canPay}
         leadingIcon={disabled && disabledMessage ? 'circle-info' : undefined}
         ariaLabel={translate(getPaymentButtonLabel(variant))}
         onClick={() => onPayClick(variant)}
      >
         {translate('invoices.invoice-summary.pay')}
      </Button>
   );

   return (
      <Box spacing="lg" backgroundColor={backgroundColor} borderRadius="sm" items="center" justify="between">
         <Box direction="column" gap="none" minWidth={150}>
            <Typography variant="body-highlight" color={canPay ? 'strong' : 'subtle'}>
               {title}
            </Typography>
            <Typography variant="heading-4" color={color}>
               {showTotal && (
                  <>
                     <Typography variant="small-highlight" color="inherit">
                        {translate('invoices.invoice-summary.payable-online')}
                     </Typography>
                     <br />
                  </>
               )}
               {format.price({ amount, currency })}
               {showTotal && (
                  <Tooltip text={translate('invoices.invoice-summary.invalid-payment-media')}>
                     <Icon icon="circle-info" color="inherit" />
                  </Tooltip>
               )}
            </Typography>
            {showTotal && (
               <Typography variant="small-highlight">
                  {translate('invoices.invoice-summary.total')}{' '}
                  {format.price({
                     amount: totalAmount,
                     currency
                  })}
               </Typography>
            )}
         </Box>
         {disabled && disabledMessage ? <Tooltip text={disabledMessage}>{PayButton}</Tooltip> : PayButton}
      </Box>
   );
};

export const InvoicePaymentSummary: FC<Props> = ({ currency, openInvoices, selectedInvoiceIds, canDeductCreditNotes, onPayClick }) => {
   const translate = useTranslations();
   const format = useFormatter();

   const totalSelectedAmount = calculateTotalOpenAmount(openInvoices.filter(({ id }) => selectedInvoiceIds.includes(id)));
   const totals = useMemo(() => {
      const totalAmount = calculateTotalOpenAmount(openInvoices);
      const totalOnlinePayableAmount = calculateTotalOpenAmount(openInvoices.filter(canSelectInvoice));
      const totalOverDue = calculateTotalPositiveDueAmount(openInvoices);
      const totalOnlinePayableOverDue = calculateTotalPositiveDueAmount(openInvoices.filter(canSelectInvoice));
      const totalCredit = calculateTotalNegativeAmount(openInvoices);

      return {
         totalAmount,
         totalOnlinePayableAmount,
         totalOverDue,
         totalOnlinePayableOverDue,
         totalCredit
      };
   }, [openInvoices]);

   const canPaySelected = !canDeductCreditNotes || totalSelectedAmount + totals.totalCredit >= 0;

   return (
      <Box direction="column">
         <BreakpointSwitch includeViews={['desktop']}>
            <Box>
               <PaymentButton
                  variant="selected"
                  amount={totalSelectedAmount}
                  currency={currency}
                  disabled={!canPaySelected}
                  disabledMessage={translate('invoices.invoice-summary.pay-disabled-message')}
                  onPayClick={onPayClick}
               />
               <PaymentButton
                  variant="open"
                  amount={totals.totalOnlinePayableAmount}
                  currency={currency}
                  totalAmount={totals.totalAmount}
                  onPayClick={onPayClick}
               />
               <PaymentButton
                  variant="overdue"
                  amount={totals.totalOnlinePayableOverDue}
                  currency={currency}
                  totalAmount={totals.totalOverDue}
                  onPayClick={onPayClick}
               />
            </Box>
         </BreakpointSwitch>
         <BreakpointSwitch excludeViews={['desktop']}>
            <Box direction="column">
               <PaymentButton
                  variant="open"
                  amount={totals.totalOnlinePayableAmount}
                  currency={currency}
                  totalAmount={totals.totalAmount}
                  onPayClick={onPayClick}
               />
               <PaymentButton
                  variant="overdue"
                  amount={totals.totalOnlinePayableOverDue}
                  currency={currency}
                  totalAmount={totals.totalOverDue}
                  onPayClick={onPayClick}
               />
               <PaymentButton
                  variant="selected"
                  amount={totalSelectedAmount}
                  currency={currency}
                  disabled={!canPaySelected}
                  disabledMessage={translate('invoices.invoice-summary.pay-disabled-message')}
                  onPayClick={onPayClick}
               />
            </Box>
         </BreakpointSwitch>
         {canDeductCreditNotes && totals.totalCredit !== 0 && (
            <Typography>
               <Icon icon="circle-info" color="inherit" /> {translate('invoices.invoice-summary.credit-notes-message')} (
               {format.price({ amount: totals.totalCredit, currency })})
            </Typography>
         )}
      </Box>
   );
};
